// Calendario de lo que sale, vuelve y está ocupado: mes, semana, día y por
// equipo. Usa la misma cuenta que la disponibilidad (lo que dice aquí es lo
// que el sistema deja o no deja rentar).
import { el, clear } from '../../utils/dom.js';
import { Rentals, Categories, Items } from '../../api/resources.js';
import { navigate } from '../../router/index.js';

const TZ = 'America/Santo_Domingo';
const SEMANA = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];
const VISTAS = [['mes', 'Mes'], ['semana', 'Semana'], ['dia', 'Día'], ['equipos', 'Por equipo']];
const DIAS_EQUIPOS = 14;

const hoyEn = (d = new Date()) => new Intl.DateTimeFormat('en-CA', { timeZone: TZ }).format(d);
const sumar = (dia, n) => { const [y, m, d] = dia.split('-').map(Number); return new Date(Date.UTC(y, m - 1, d + n)).toISOString().slice(0, 10); };
const inicioDia = (dia) => new Date(`${dia}T00:00:00-04:00`).toISOString();
const lunesDe = (dia) => { const [y, m, d] = dia.split('-').map(Number); const w = new Date(Date.UTC(y, m - 1, d)).getUTCDay(); return sumar(dia, -((w + 6) % 7)); };
const diaDe = (iso) => hoyEn(new Date(iso));
const fmt = (o, dia) => new Intl.DateTimeFormat('es-DO', { ...o, timeZone: 'UTC' }).format(new Date(`${dia}T12:00:00Z`));
const hora = (iso) => new Intl.DateTimeFormat('es-DO', { hour: 'numeric', minute: '2-digit', timeZone: TZ }).format(new Date(iso));

const ETAPA = { cotizacion: 'Cotización', pendiente: 'Por confirmar', confirmada: 'Confirmada', en_curso: 'Entregada', devuelta: 'Devuelta', cerrada: 'Cerrada' };
function tono(e) {
  if (e.atrasado) return 'cal-t-atrasada';
  if (e.tipo === 'renta') return `cal-t-${e.etapa}`;
  return `cal-t-${e.tipo}`;
}

export default async function CalendarioPage(outlet, ctx) {
  clear(outlet);
  const q = ctx.query || {};
  const vista = VISTAS.some(([v]) => v === q.vista) ? q.vista : 'mes';
  const hoy = hoyEn();
  const fecha = /^\d{4}-\d{2}-\d{2}$/.test(q.fecha || '') ? q.fecha : hoy;
  const filtros = { tipo: q.tipo || '', item_id: q.equipo || '', categoria_id: q.categoria || '' };

  const ir = (cambios) => {
    const p = new URLSearchParams({ vista, fecha, ...(filtros.tipo && { tipo: filtros.tipo }), ...(filtros.item_id && { equipo: filtros.item_id }), ...(filtros.categoria_id && { categoria: filtros.categoria_id }) });
    for (const [k, v] of Object.entries(cambios)) { if (v) p.set(k, v); else p.delete(k); }
    navigate(`/calendario?${p.toString()}`);
  };

  // Rango visible.
  const [an, me] = fecha.split('-').map(Number);
  const primero = `${fecha.slice(0, 8)}01`;
  const finMes = new Date(Date.UTC(an, me, 0)).toISOString().slice(0, 10);
  const rango = vista === 'mes' ? [lunesDe(primero), sumar(lunesDe(finMes), 7)]
    : vista === 'semana' ? [lunesDe(fecha), sumar(lunesDe(fecha), 7)]
      : vista === 'dia' ? [fecha, sumar(fecha, 1)]
        : [lunesDe(fecha), sumar(lunesDe(fecha), DIAS_EQUIPOS)];
  const dias = [];
  for (let d = rango[0]; d < rango[1]; d = sumar(d, 1)) dias.push(d);

  const [cats, items, eventos, filas] = await Promise.all([
    Categories.list({ limit: 500 }).then((r) => r.data || []).catch(() => []),
    Items.list({ limit: 500 }).then((r) => r.data || []).catch(() => []),
    vista === 'equipos' ? [] : Rentals.calendario({ desde: inicioDia(rango[0]), hasta: inicioDia(rango[1]), tipo: filtros.tipo || undefined, item_id: filtros.item_id || undefined, categoria_id: filtros.categoria_id || undefined }),
    ['equipos', 'dia'].includes(vista) ? Rentals.ocupacion({ desde: rango[0], dias: dias.length, item_id: filtros.item_id || undefined, categoria_id: filtros.categoria_id || undefined }) : [],
  ]);

  const paso = vista === 'mes' ? null : vista === 'semana' ? 7 : vista === 'dia' ? 1 : DIAS_EQUIPOS;
  const anterior = paso ? sumar(fecha, -paso) : new Date(Date.UTC(an, me - 2, 1)).toISOString().slice(0, 10);
  const siguiente = paso ? sumar(fecha, paso) : new Date(Date.UTC(an, me, 1)).toISOString().slice(0, 10);
  const titulo = vista === 'mes' ? fmt({ month: 'long', year: 'numeric' }, fecha)
    : vista === 'dia' ? fmt({ weekday: 'long', day: 'numeric', month: 'long' }, fecha)
      : `${fmt({ day: 'numeric', month: 'short' }, dias[0])} – ${fmt({ day: 'numeric', month: 'short', year: 'numeric' }, dias[dias.length - 1])}`;

  outlet.appendChild(el('div', { class: 'page-head' },
    el('div', {}, el('h1', {}, 'Calendario'), el('div', { class: 'muted' }, 'Qué sale, qué vuelve y qué está ocupado cada día')),
    el('div', { class: 'flex gap-2 wrap' },
      el('a', { href: '#/calendario/google', class: 'btn' }, 'Google Calendar'),
      el('a', { href: '#/rentals', class: 'btn ghost' }, '← Rentas')),
  ));

  const sel = (opts, valor, onchange, label) => el('select', { class: 'select', 'aria-label': label, onchange: (e) => onchange(e.target.value) },
    ...opts.map(([v, t]) => { const o = el('option', { value: v }, t); if (v === valor) o.selected = true; return o; }));
  outlet.appendChild(el('div', { class: 'toolbar wrap' },
    el('button', { class: 'btn', 'aria-label': 'Anterior', onclick: () => ir({ fecha: anterior }) }, '←'),
    el('button', { class: 'btn', onclick: () => ir({ fecha: hoy }) }, 'Hoy'),
    el('button', { class: 'btn', 'aria-label': 'Siguiente', onclick: () => ir({ fecha: siguiente }) }, '→'),
    el('strong', { class: 'cal-titulo' }, titulo),
    el('div', { class: 'flex-1' }),
    el('div', { class: 'cal-vistas', role: 'tablist' }, ...VISTAS.map(([v, t]) =>
      el('button', { class: `btn ${vista === v ? 'primary' : ''}`, role: 'tab', 'aria-selected': String(vista === v), onclick: () => ir({ vista: v }) }, t))),
  ));
  outlet.appendChild(el('div', { class: 'toolbar wrap' },
    vista !== 'equipos' ? sel([['', 'Todo'], ['renta', 'Rentas'], ['reserva', 'Reservas'], ['prestamo', 'Préstamos'], ['mantenimiento', 'Mantenimiento']], filtros.tipo, (v) => ir({ tipo: v }), 'Tipo') : null,
    sel([['', 'Todas las categorías'], ...cats.map((c) => [c.id, c.name])], filtros.categoria_id, (v) => ir({ categoria: v, equipo: '' }), 'Categoría'),
    sel([['', 'Todos los equipos'], ...items.filter((i) => !filtros.categoria_id || i.category_id === filtros.categoria_id).map((i) => [i.id, i.name])], filtros.item_id, (v) => ir({ equipo: v }), 'Equipo'),
  ));

  if (vista !== 'equipos') {
    outlet.appendChild(el('div', { class: 'cal-leyenda mb-3' },
      ...[['cal-t-pendiente', 'Por confirmar'], ['cal-t-confirmada', 'Confirmada'], ['cal-t-en_curso', 'Entregada'], ['cal-t-atrasada', 'Atrasada'],
        ['cal-t-devuelta', 'Devuelta / cerrada'], ['cal-t-reserva', 'Reserva'], ['cal-t-prestamo', 'Préstamo'], ['cal-t-mantenimiento', 'Mantenimiento']]
        .map(([c, t]) => el('span', {}, el('i', { class: `cal-punto ${c}` }), t)),
      el('span', {}, '↗ sale · ↙ vuelve')));
  }

  const delDia = (dia) => { const a = inicioDia(dia); const b = inicioDia(sumar(dia, 1)); return eventos.filter((e) => e.inicio < b && e.fin > a); };
  const sale = (e, dia) => diaDe(e.inicio) === dia;
  const vuelve = (e, dia) => !e.atrasado && diaDe(e.fin) === dia;
  const chip = (e, dia) => el('a', { href: e.href, class: `cal-chip ${tono(e)}`, title: `${e.titulo} · ${e.equipos}` },
    `${sale(e, dia) ? '↗ ' : vuelve(e, dia) ? '↙ ' : ''}${e.codigo ? `${e.codigo} · ` : ''}${e.titulo}`);
  const verDia = (dia) => ir({ vista: 'dia', fecha: dia });

  if (vista === 'mes') {
    const grid = el('div', { class: 'card cal-mes' },
      ...SEMANA.map((d) => el('div', { class: 'cal-mes-head' }, d)),
      ...dias.map((dia) => {
        const evs = delDia(dia);
        return el('div', { class: `cal-mes-dia ${dia.slice(0, 7) !== fecha.slice(0, 7) ? 'fuera' : ''}` },
          el('button', { class: `cal-num-btn ${dia === hoy ? 'hoy' : ''}`, onclick: () => verDia(dia) }, String(Number(dia.slice(8)))),
          el('div', { class: 'cal-chips' }, ...evs.slice(0, 3).map((e) => chip(e, dia)),
            evs.length > 3 ? el('button', { class: 'cal-mas', onclick: () => verDia(dia) }, `+${evs.length - 3} más`) : null),
          evs.length ? el('button', { class: 'cal-puntos', 'aria-label': `${evs.length} en este día`, onclick: () => verDia(dia) },
            ...evs.slice(0, 4).map((e) => el('i', { class: `cal-punto ${tono(e)}` }))) : null);
      }));
    outlet.appendChild(grid);
  }

  if (vista === 'semana') {
    outlet.appendChild(el('div', { class: 'cal-semana' }, ...dias.map((dia, i) => {
      const evs = delDia(dia);
      return el('div', { class: `card cal-sem-dia ${dia === hoy ? 'hoy' : ''}` },
        el('button', { class: 'cal-sem-head', onclick: () => verDia(dia) }, el('span', {}, SEMANA[i]), el('strong', {}, fmt({ day: 'numeric', month: 'short' }, dia))),
        evs.length ? null : el('p', { class: 'muted text-sm' }, 'Libre'),
        ...evs.map((e) => el('div', { class: 'mb-2' }, chip(e, dia),
          el('div', { class: 'text-xs muted' }, sale(e, dia) ? `Sale ${hora(e.inicio)}` : vuelve(e, dia) ? `Vuelve ${hora(e.fin)}` : e.atrasado ? 'Atrasada' : 'Afuera'))));
    })));
  }

  if (vista === 'dia') {
    const evs = delDia(fecha);
    const grupo = (t, lista, h) => el('section', { class: 'card mb-4' },
      el('h3', { class: 'rp-h' }, `${t} (${lista.length})`),
      lista.length ? el('ul', { class: 'rp-lista' }, ...lista.map((e) => el('li', { class: 'flex gap-3 items-start' },
        el('span', { class: 'text-xs muted', style: 'width:70px' }, h(e)),
        el('div', { class: 'flex-1' }, el('a', { href: e.href, class: 'text-bold' }, `${e.codigo ? `${e.codigo} · ` : ''}${e.titulo}`),
          el('div', { class: 'text-xs muted' }, e.equipos)),
        el('span', { class: `cal-chip ${tono(e)}` }, e.atrasado ? 'Atrasada' : e.tipo === 'renta' ? (ETAPA[e.etapa] || e.etapa) : e.tipo)))) : el('p', { class: 'muted' }, 'Nada.'));
    const ocupados = (filas || []).filter((f) => (f.dias[0]?.ocupado || 0) > 0);
    outlet.appendChild(el('div', { class: 'rp-grid' },
      el('div', { class: 'rp-main' },
        grupo('Salen', evs.filter((e) => sale(e, fecha)), (e) => hora(e.inicio)),
        grupo('Vuelven', evs.filter((e) => vuelve(e, fecha) && !sale(e, fecha)), (e) => hora(e.fin)),
        grupo('Afuera o apartado todo el día', evs.filter((e) => !sale(e, fecha) && !vuelve(e, fecha)), (e) => (e.atrasado ? 'Atrasada' : ''))),
      el('aside', { class: 'rp-aside' }, el('section', { class: 'card' },
        el('h3', { class: 'rp-h' }, 'Equipos ocupados ese día'),
        ocupados.length ? el('ul', { class: 'rp-lista' }, ...ocupados.map((f) => el('li', { class: 'flex justify-between gap-2' },
          el('a', { href: `#/inventory/${f.id}` }, f.nombre),
          el('span', { class: f.dias[0].ocupado >= f.dias[0].total ? 'rp-rojo text-bold' : 'muted' }, `${f.dias[0].ocupado} de ${f.dias[0].total}`)))) : el('p', { class: 'muted' }, 'Todo libre.'))),
    ));
  }

  if (vista === 'equipos') {
    outlet.appendChild(el('p', { class: 'muted text-sm' }, 'Cada casilla dice cuántos están ocupados de los que tienes ese día. Verde: libre · amarillo: en parte · rojo: agotado.'));
    if (!filas.length) { outlet.appendChild(el('div', { class: 'card' }, el('p', { class: 'muted' }, 'No hay equipos con esos filtros.'))); return; }
    outlet.appendChild(el('div', { class: 'card cal-tabla-wrap' }, el('table', { class: 'cal-tabla' },
      el('thead', {}, el('tr', {}, el('th', { class: 'cal-fijo' }, 'Equipo'),
        ...dias.map((d) => el('th', { class: d === hoy ? 'hoy' : '' }, el('button', { class: 'cal-th-btn', onclick: () => verDia(d) },
          el('span', {}, fmt({ weekday: 'short' }, d).slice(0, 3)), el('strong', {}, String(Number(d.slice(8))))))))),
      el('tbody', {}, ...filas.map((f) => el('tr', {},
        el('th', { class: 'cal-fijo', scope: 'row' }, el('a', { href: `#/inventory/${f.id}`, title: f.nombre }, f.nombre)),
        ...f.dias.map((c) => el('td', {}, el('span', {
          class: `cal-celda ${c.total === 0 ? 'nada' : c.ocupado === 0 ? 'libre' : c.ocupado >= c.total ? 'lleno' : 'parte'}`,
          title: `${c.ocupado} de ${c.total} ocupados`,
        }, c.total === 0 ? '—' : c.ocupado === 0 ? '' : `${c.ocupado}/${c.total}`))))))),
    ));
  }
}
